import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Auth from "service/AuthService";
import { useAuth } from "provider/AuthContext";
import { API_URL } from "helper/Constants";
import { FETCH_DATE_AFTER_SELECTION, isEmpty } from "helper/Functions";
import SportbookService from "service/SportsbookService";
import CustomPagingTable from "components/ui/tables/CustomPagingTable";
import SelectDropDown from "components/ui/forms/SelectDropDown";
import DateFilter from "../FilterFormComponents/DateFilter";
import { ReactComponent as IconSearch } from "assets/icons/icon-search.svg";
import Modal from "components/ui/Modal";
import SportBetBookDetailModal from "./SportBetBookDetailModal";
import { FaInfoCircle } from "react-icons/fa";
import SectionLoader from "components/ui/loaders/SectionLoader";

/** ---------------------------
 * Debounce utility (promise-friendly)
 * - Coalesces multiple calls into one (latest args win)
 * - Resolves all pending promises with the same result
 * -------------------------- */
const debouncePromise = (fn, delay = 500) => {
  let timer;
  let resolvers = [];
  return (...args) =>
    new Promise((resolve) => {
      if (timer) clearTimeout(timer);
      resolvers.push(resolve);

      timer = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolvers.forEach((r) => r(result));
        } finally {
          resolvers = [];
        }
      }, delay);
    });
};

const SportBetBook = () => {
  // Services memoized to avoid re-instantiation each render
  const sportBookService = useMemo(() => new SportbookService(API_URL), []);
  const authServ = useMemo(() => new Auth(API_URL), []);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedDate, setSelectedDate] = useState("today");
  const [customDate, setCustomDate] = useState("");
  const [reportData, setReportData] = useState([]);

  const [isRowDetailModalOpen, setIsRowDetailModalOpen] = useState(false);
  const [selectedRowDetail, setSelectedRowDetail] = useState(null);

  // PAGINATION STATES
  const [betId, setBetId] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, onPaginationChange] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const [message, setMessage] = useState(null);
  const { user } = useAuth();

  /** ---------------------------------------
   * Fetch users (raw) — keeps your role logic
   * -------------------------------------- */
  const fetchUsers = async (inputValue, callback) => {
    if (!user) {
      callback([]);
      return;
    }
    try {
      let response;
      let options = [];

      if (user.role === "Owner") {
        response = await authServ.getAllUsers();
        options = response.users
          .filter(
            (fi) =>
              fi.username !== user.username &&
              fi.username.toLowerCase().includes(inputValue.toLowerCase())
          )
          .map((item) => ({
            value: item._id,
            label: `${item.username} -> ${item?.role} -> ${item?.balance?.toFixed(2)}`,
          }));
      } else {
        response = await authServ.getUsersByCreatorId(user._id);
        options =
          response.users.children
            ?.filter(
              (fi) =>
                fi.username !== user.username &&
                fi.username.toLowerCase().includes(inputValue.toLowerCase())
            )
            .map((item) => ({
              value: item._id,
              label: `${item.username} -> ${item?.role} -> ${item?.balance?.toFixed(2)}`,
            })) || [];
      }

      callback(options);
    } catch (error) {
      console.error("Error fetching users:", error);
      callback([]);

      if (error.response?.status === 401) {
        setMessage("Authorization failed. Please log in again.");
      } else {
        setMessage("Error fetching users. Please try again later.");
      }
    }
  };

  /** -------------------------------------------------------
   * Debounced wrapper around fetchUsers (latest input wins)
   * ------------------------------------------------------ */
  const debouncedFetchUsers = useMemo(
    () => debouncePromise(fetchUsers, 600),
    // Depend on role and service instance; not on user object to avoid recreating too often
    [user?.role, authServ]
  );

  /** -------------------------------------------------------
   * Bets History
   * ------------------------------------------------------ */
  const fetchSportbookBetsHistory = useCallback(
    async (updatedPageIndex) => {
      setLoading(true);
      if (isEmpty(selectedUser)) {
        setMessage("Please select a user before searching.");
        setLoading(false); // ensure we don't get stuck in loading
        return;
      }

      try {
        let dateOption;
        if (selectedDate === "custom" && customDate) {
          const [startDate, endDate] = customDate
            .split(" - ")
            .map((date) => new Date(date).toISOString().split("T")[0]);
          if (!startDate || !endDate) {
            setMessage("Invalid date range selected.");
            setLoading(false);
            return;
          }
          dateOption = { startDate, endDate };
        } else {
          const { startDate, endDate } = FETCH_DATE_AFTER_SELECTION(selectedDate, customDate);
          dateOption = { startDate, endDate };
        }

        const dto = {
          userId: selectedUser?.value,
          ticketCode: betId,
          startDate: dateOption.startDate,
          endDate: dateOption.endDate,
          page: updatedPageIndex + 1,
          limit: pagination.pageSize,
        };

        const result = await sportBookService.fetchSportbookBets(dto);
        if (result) {
          setTotalItems(result.pagination.totalItems);
          setReportData(result.tickets);
          setMessage("");
        } else {
          console.error("Error from API:", result.message);
          setMessage(result.message);
        }
      } catch (error) {
        console.error("Error fetching transfer history:", error);
        setMessage("An error occurred while fetching transfer history.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, selectedDate, customDate, selectedUser, betId, sportBookService]
  );

  const handlePagination = (pageable) => {
    onPaginationChange((prev) => {
      const nextPageIndex = pageable(prev).pageIndex;
      fetchSportbookBetsHistory(nextPageIndex);
      return pageable(prev);
    });
  };

  const handleSearch = () => {
    fetchSportbookBetsHistory(pagination.pageIndex);
  };

  /** ---------------------------------------
   * Input handlers for async dropdown
   * -------------------------------------- */
  const handleInputChange = (value) => {
    setSearchTerm(value);
    setShowSuggestions(value.length >= 3);
  };

  const handleMenuClose = () => setShowSuggestions(false);

  const handleReset = () => {
    setSelectedUser("");
    setSearchTerm("");
    setBetId("");
    setShowSuggestions(false);
  };

  /** ---------------------------------------
   * Table setup
   * -------------------------------------- */
  const tableData = reportData?.map((item) => ({ ...item }));

  const tableColumns = useMemo(
    () => [
      { accessorKey: "userId", header: "User ID", size: 100 },
      {
        accessorKey: "currency",
        header: "Currency",
        size: 100,
        Cell: () => <span className="text-slate-500">TND</span>,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 50,
        Cell: ({ cell }) => <>{Math.abs(cell.row.original.amount).toFixed(2)}</>,
      },
      {
        accessorKey: "odds",
        header: "Odds",
        size: 50,
        Cell: ({ cell }) =>
          cell.row.original?.odds
            ?.map((odds) => odds.odd.oddValue)
            ?.reduce((acc, val) => acc * val, 1)
            .toFixed(2),
      },
      {
        accessorKey: "possibleWin",
        header: "Possible Win",
        size: 50,
        Cell: ({ cell }) => (
          <>
            {Math.abs(
              cell.row.original.amount *
                cell.row.original?.odds?.map((odds) => odds.odd.oddValue)?.reduce((acc, val) => acc * val, 1)
            ).toFixed(2)}
          </>
        ),
      },
      { accessorKey: "ticketCode", header: "Ticket Code", size: 200 },
      {
        accessorKey: "status",
        header: "Status",
        size: 50,
        Cell: ({ cell }) => {
          const statusColors = {
            reserved: "bg-red-200 text-red-700",
            rollbacked: "bg-yellow-200 text-yellow-700",
            confirmed: "bg-green-200 text-green-700",
            canceled: "bg-orange-200 text-orange-700",
            concluded: "bg-slate-200 text-slate-700",
            running: "bg-blue-200 text-blue-700",
          };
          const status = cell.row.original?.status?.toLowerCase();
          return (
            <div className="flex">
              {statusColors[status] ? (
                <div className={`${statusColors[status]} font-bold text-center rounded px-2 capitalize`}>{status}</div>
              ) : (
                status
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "state",
        header: "Bet Status",
        size: 50,
        Cell: ({ cell }) => {
          const statusColors = {
            running: "bg-blue-200 text-blue-700",
            lost: "bg-slate-200 text-gray-700",
            won: "bg-green-200 text-green-700",
            void: "bg-yellow-200 text-yellow-700",
            cancelled: "bg-orange-200 text-orange-700",
            rejected: "bg-red-200 text-red-700",
            "cash-out": "bg-purple-200 text-purple-700",
            reserved: "bg-red-200 text-red-700",
            concluded: "bg-slate-200 text-slate-700",
            confirmed: "bg-green-200 text-green-700",
          };
          const status = cell.row.original?.state?.toLowerCase();
          return (
            <div className="flex">
              {statusColors[status] ? (
                <div className={`${statusColors[status]} font-bold text-center rounded px-2 capitalize`}>{status}</div>
              ) : (
                status
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Date",
        size: 150,
        Cell: ({ cell }) => moment?.tz(cell.row.original?.createdAt, "UTC")?.format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        accessorKey: "#",
        header: "Actions",
        size: 50,
        Cell: ({ cell }) => (
          <div>
            <button
              className="cursor-pointer text-primary/70 hover:text-primary"
              onClick={() => {
                setIsRowDetailModalOpen(true);
                setSelectedRowDetail(cell.row.original);
              }}
            >
              <FaInfoCircle size={20} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const statusColors = {
    reserved: "bg-red-200 text-red-700",
    rollbacked: "bg-yellow-200 text-yellow-700",
    confirmed: "bg-green-200 text-green-700",
    canceled: "bg-orange-200 text-orange-700",
    concluded: "bg-slate-200 text-slate-700",
    running: "bg-blue-200 text-blue-700",
  };

  return (
    <div className="flex flex-col">
      <header className="rounded-lg sm:text-start text-center bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">Sport Betting Report</h1>
      </header>

      <div className="w-full rounded-lg">
        <div className="flex-1 pt-4">
          <DateFilter
            title="Transaction Date"
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            setCustomDate={setCustomDate}
          />
        </div>

        <div className="flex flex-row lg:flex-row w-full flex-wrap gap-2 sm:gap-0 lg:gap-4 rtl:space-x-reverse">
          <div className="w-full sm:w-[10%] lg:w-[48%] 2xl:w-[49%]">
            <SelectDropDown
              label="Choose User"
              placeholder={"Type 3+ letters"}
              selectedOption={selectedUser ? selectedUser : []}
              // 🔸 Only invoke API when >= 3 chars, else clear immediately.
              loadOptions={(inputValue, callback) => {
                if (!inputValue || inputValue.length < 3) {
                  callback([]);
                  return;
                }
                // Debounced call (latest input wins)
                debouncedFetchUsers(inputValue, callback);
              }}
              handleChange={(option) => setSelectedUser(option)}
              inputValue={searchTerm || ""}
              onInputChange={handleInputChange}
              menuIsOpen={showSuggestions || false}
              onMenuClose={handleMenuClose}
              isSearchable={true}
            />
          </div>

          <div className="w-full sm:w-[10%] lg:w-[48%] 2xl:w-[49%]">
            <label className="block text-md ml-3 text-[#242424]">Bet Id</label>
            <input
              name="betID"
              id="betID"
              autoComplete="off"
              data-1p-ignore="true"
              className="text-black w-full rounded-md border px-2 mt-[0.4px] py-1.5"
              value={betId}
              onChange={(e) => setBetId(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="lg:flex gap-3 mt-7 w-full lg:max-w-sm">
        <button
          className={`bg-primary hover:bg-primary/80 text-black w-full lg:max-w-36 font-bold py-2 rounded-lg ${
            !isEmpty(selectedUser) ? " text-black cursor-pointer" : "bg-[#f2c41a] text-black cursor-not-allowed opacity-50"
          }`}
          type="button"
          onClick={handleSearch}
          disabled={isEmpty(selectedUser)}
        >
          SEARCH
        </button>
        <button
          className={`border border-black mt-4 sm:mt-0 lg:max-w-36 rounded-lg text-md hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 w-full`}
          type="reset"
          onClick={handleReset}
        >
          RESET
        </button>
      </div>

      <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />
      {message && <div className="mt-4 p-2 text-center text-red-600">{message}</div>}

      {reportData.length > 0 ? (
        <CustomPagingTable
          loading={loading}
          columns={tableColumns}
          data={tableData ? tableData : []}
          rowCount={totalItems}
          pageIndex={pagination.pageIndex}
          state={{ pagination }}
          onPaginationChange={handlePagination}
        />
      ) : loading ? (
        <div className="flex justify-center py-8">
          <SectionLoader />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
          <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
            <IconSearch />
          </div>
          <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
            Search all available records by selecting one of the filters above!
          </span>
        </div>
      )}

      {isRowDetailModalOpen && selectedRowDetail ? (
        <Modal
          isOpen={isRowDetailModalOpen}
          onClose={() => setIsRowDetailModalOpen(false)}
          className="w-full max-w-[80rem] overflow-y-auto "
          title={
            <>
              <div className="px-4 py-4 flex items-center">
                <div className="font-bold md:text-2xl text-xl">Ticket Details</div>
                {selectedRowDetail.status && (
                  <div
                    className={`${
                      (statusColors[selectedRowDetail.status.toLowerCase()] ||
                        "bg-slate-200 text-slate-700")
                    } font-bold text-center rounded ml-3 px-2 capitalize`}
                  >
                    {selectedRowDetail.status}
                  </div>
                )}
              </div>
            </>
          }
        >
          <SportBetBookDetailModal data={selectedRowDetail} />
        </Modal>
      ) : null}
    </div>
  );
};

export default SportBetBook;
